import {
  and,
  chat_message,
  chat_thread,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
} from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { canonicalPair } from "./pair";
import { getThreadByPairQuery } from "./queries";

export interface OpenThreadOpts {
  meName?: string | null;
  meImage?: string | null;
  otherName?: string | null;
  otherImage?: string | null;
}

export interface OpenThreadResult {
  id: string;
  /** true si se creó un hilo nuevo (false si ya existía). */
  created: boolean;
}

/**
 * 💬 ABRIR HILO — UPDATE-or-INSERT por par canónico (dedup con independencia de
 * quién inicie). El servidor valida que el actor es participante y estampa los
 * perfiles; aquí resolvemos el hilo existente o creamos uno (patrón susurro).
 */
export async function openThreadMutation(
  db: PowerSyncDrizzleDB,
  meId: string,
  otherId: string,
  opts?: OpenThreadOpts,
): Promise<OpenThreadResult> {
  const existing = await getThreadByPairQuery(db, meId, otherId);
  if (existing[0]) {
    return { id: existing[0].id, created: false };
  }
  const [ua, ub] = canonicalPair(meId, otherId);
  const aIsMe = ua === meId;
  const id = generateUUID();
  const now = nowISO();
  await db.insert(chat_thread).values({
    id,
    user_a: ua,
    user_b: ub,
    a_name: aIsMe ? (opts?.meName ?? null) : (opts?.otherName ?? null),
    a_image: aIsMe ? (opts?.meImage ?? null) : (opts?.otherImage ?? null),
    b_name: aIsMe ? (opts?.otherName ?? null) : (opts?.meName ?? null),
    b_image: aIsMe ? (opts?.otherImage ?? null) : (opts?.meImage ?? null),
    created_at: now,
    updated_at: now,
  });
  return { id, created: true };
}

/**
 * 💬 ENVIAR MENSAJE. El `recipientId` es el otro participante (lo derivamos en
 * la UI vía `otherParticipant`); el servidor lo RE-DERIVA de la pertenencia al
 * hilo (no confía en el cliente). Refresca el preview del hilo localmente.
 */
export async function sendMessageMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
  senderId: string,
  recipientId: string,
  body: string,
): Promise<{ id: string }> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(chat_message).values({
    id,
    thread_id: threadId,
    sender_id: senderId,
    recipient_id: recipientId,
    body,
    read_at: null,
    created_at: now,
  });
  await db
    .update(chat_thread)
    .set({ last_message_at: now, last_message_preview: body, updated_at: now })
    .where(eq(chat_thread.id, threadId));
  return { id };
}

/** Marca leídos los mensajes RECIBIDOS de un hilo (yo soy el recipient). */
export async function markThreadReadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
  meId: string,
): Promise<void> {
  await db
    .update(chat_message)
    .set({ read_at: nowISO() })
    .where(
      and(
        eq(chat_message.thread_id, threadId),
        eq(chat_message.recipient_id, meId),
        isNull(chat_message.read_at),
      ),
    );
}
